import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import LoadingSpinner from './LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PublicFeed: React.FC<{ user: any }> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'all' | 'spells' | 'grimoire' | 'journal' | 'questions'>('all');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ title: '', content: '' });

  const fetchFeed = useCallback(async () => {
    if (!user) {
        setLoading(false);
        return;
    }
    setLoading(true);
    try {
      let endpoint = '';
      if (activeTab === 'all') endpoint = '/public/all';
      else if (activeTab === 'spells') endpoint = '/public/spells';
      else if (activeTab === 'grimoire') endpoint = '/public/grimoire';
      else if (activeTab === 'journal') endpoint = '/public/journal';
      else endpoint = '/questions';
      
      const res = await axios.get(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setItems(res.data);
    } catch (err) {
      console.error('Error fetching public feed:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, user]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const handleLike = async (item: any) => {
    if (!user) {
      alert("You must be logged in to like shared wisdom.");
      return;
    }
    try {
      let likePayload = {};
      const type = activeTab === 'all' ? item.item_type : activeTab;

      if (type === 'spells') likePayload = { spell_id: item.id };
      else if (type === 'grimoire') likePayload = { grimoire_id: item.id };
      else if (type === 'journal') likePayload = { journal_id: item.id };
      else likePayload = { question_id: item.id };

      await axios.post(`${API_URL}/likes`, 
        likePayload,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchFeed(); // Refresh to get new like count
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handlePostQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      await axios.post(`${API_URL}/questions`, 
        newQuestion,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setNewQuestion({ title: '', content: '' });
      setShowQuestionForm(false);
      fetchFeed();
    } catch (err) {
      console.error('Error posting question:', err);
    }
  };

  return (
    <div className="content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Community Circle</h2>
        {(activeTab === 'questions' || activeTab === 'all') && user && (
          <button onClick={() => setShowQuestionForm(!showQuestionForm)} className="btn-gold">
            {showQuestionForm ? 'CANCEL' : 'ASK A QUESTION'}
          </button>
        )}
      </div>
      <p>Discover shared wisdom and successful workings from fellow practitioners.</p>
      
      <div className="tab-container" style={{ flexWrap: 'wrap' }}>
        <div 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`} 
          onClick={() => setActiveTab('all')}
        >
          ALL WISDOM
        </div>
        <div 
          className={`tab ${activeTab === 'spells' ? 'active' : ''}`} 
          onClick={() => setActiveTab('spells')}
        >
          SHARED SPELLS
        </div>
        <div 
          className={`tab ${activeTab === 'grimoire' ? 'active' : ''}`} 
          onClick={() => setActiveTab('grimoire')}
        >
          SHARED GRIMOIRE
        </div>
        <div 
          className={`tab ${activeTab === 'journal' ? 'active' : ''}`} 
          onClick={() => setActiveTab('journal')}
        >
          SHARED JOURNAL
        </div>
        <div 
          className={`tab ${activeTab === 'questions' ? 'active' : ''}`} 
          onClick={() => setActiveTab('questions')}
        >
          COMMUNITY QUESTIONS
        </div>
      </div>

      {showQuestionForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>Ask the Circle</h3>
          <form onSubmit={handlePostQuestion}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Question Title</label>
              <input 
                type="text" 
                placeholder="What is your question?" 
                value={newQuestion.title}
                onChange={e => setNewQuestion({...newQuestion, title: e.target.value})}
                required
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Detail / Body</label>
              <textarea 
                placeholder="Provide more context for your question..." 
                value={newQuestion.content}
                onChange={e => setNewQuestion({...newQuestion, content: e.target.value})}
                style={{ width: '100%', minHeight: '100px', padding: '0.5rem' }}
                required
              ></textarea>
            </div>
            <button type="submit">POST QUESTION</button>
          </form>
        </div>
      )}

      {loading ? (
        <LoadingSpinner message="Gleaning wisdom from the ether..." />
      ) : (
        <div className="grid">
          {items.length > 0 ? (
            items.map((item: any) => {
              const currentType = activeTab === 'all' ? item.item_type : activeTab;
              return (
                <div key={`${currentType}-${item.id}`} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Link to={`/profile/${item.username}`} className="badge" style={{ textDecoration: 'none', color: 'inherit', border: '1px solid var(--gold)' }}>
                        {currentType === 'questions' ? `Asked by ${item.username}` : `Shared by ${item.username}`}
                      </Link>
                      {activeTab === 'all' && (
                        <span className="badge" style={{ backgroundColor: 'var(--mystic-purple)', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                          {currentType}
                        </span>
                      )}
                    </div>
                    <button 
                      onClick={() => handleLike(item)} 
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem', background: 'none', border: '1px solid var(--gold)' }}
                    >
                      ⭐ {item.like_count}
                    </button>
                  </div>
                  <h3>{item.name || item.title || `Journal Entry - ${item.date}`}</h3>
                    {currentType === 'spells' ? (
                      <>
                        <p><strong>Intent:</strong> {item.intent}</p>
                        <div style={{ marginBottom: '0.5rem' }}>
                          {item.lunar_phase && <span className="badge">🌙 {item.lunar_phase}</span>}
                        </div>
                        <p><strong>Timing:</strong> {item.timing}</p>
                        {item.results && <p><strong>Results:</strong> {item.results}</p>}
                      </>
                    ) : currentType === 'grimoire' ? (
                      <>
                        <span className="badge" style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)' }}>{item.category}</span>
                        <div className="rich-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }} />
                      </>
                    ) : currentType === 'journal' ? (
                      <>
                        <div style={{ marginBottom: '0.5rem' }}>
                          {item.lunar_phase && <span className="badge">🌙 {item.lunar_phase}</span>}
                        </div>
                        <div className="rich-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }} />
                      </>
                    ) : (
                    <>
                      <p>{item.content}</p>
                      <p style={{ fontSize: '0.8rem', opacity: 0.7 }}>{item.reply_count} Replies</p>
                    </>
                  )}
                  <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(212, 175, 55, 0.2)', paddingTop: '1rem' }}>
                      <Link to={`/public/${currentType}/${item.id}`} className="btn-gold" style={{ display: 'inline-block', textDecoration: 'none', padding: '0.4rem 1rem', fontSize: '0.9rem' }}>
                          {currentType === 'questions' ? 'Join Discussion' : 'View & Discuss'}
                      </Link>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No {activeTab === 'all' ? 'shared wisdom' : (activeTab === 'questions' ? 'questions' : `shared ${activeTab}`)} found in the circle yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicFeed;
